import React, { useEffect, useRef } from 'react'
import { debugLogForms } from '@app-src/debug'
import { NativeLeadType } from '@app-src/data/leads/types'
import { FormDetailsType, MvhColorsType } from '@app-src/types'
import {
  EXTRA_SEND_TO_PHONE,
  EXTRA_SUBSCRIBE_TO_PRICE_DROPS
} from './constants'
import {
  ensureFormDetailsAccountId,
  validateFormDetailParameters
} from './utils'

/**
 * useShowFormApi
 * @description this hook sets up the "showForm" API so it can be called by other non-React FEs
 * @param {Object} activeModalFormDetails - the active details of the modal version of the form
 * @param {Func} setActiveEmbedFormDetails - the React state setter for the current active embedded form details
 * @param {Func} setActiveModalFormDetails - the React state setter for the current active modal form details
 * @param {Func} setModalExtraFlags - the React state setter for whether to include price drop subscription with modal submission
 * @param {Func} setMvhColors - the React state setter for the Mvh color overrides
 * @param {Ref} submittedModalLeadRef - details on the modal lead which has been submitted, if any
 */
export function useShowFormApi(
  activeModalFormDetails: FormDetailsType | undefined,
  setActiveEmbedFormDetails: React.Dispatch<
    React.SetStateAction<FormDetailsType | undefined>
  >,
  setActiveModalFormDetails: React.Dispatch<
    React.SetStateAction<FormDetailsType | undefined>
  >,
  setModalExtraFlags: React.Dispatch<React.SetStateAction<number>>,
  setMvhColors: React.Dispatch<
    React.SetStateAction<MvhColorsType | undefined | null>
  >,
  submittedModalLeadRef: React.MutableRefObject<NativeLeadType | undefined>
) {
  const promiseResolveRef = useRef<
    (submittedLead: NativeLeadType | undefined) => void
  >()

  // this effect sets up the lead forms API
  useEffect(() => {
    debugLogForms('setting up API')

    // @ts-ignore
    if (!window._pureCarsLeadForm) {
      // @ts-ignore
      window._pureCarsLeadForm = {}
    }

    /**
     * overrideColors
     * @description you can set this to override colors on the form
     * @param {Object} mvhColors - the color overrides to specify.  Specify null to prevent oem-specific colors to be used
     */
    // @ts-ignore
    window._pureCarsLeadForm.overrideColors = (
      mvhColors: MvhColorsType | null
    ): void => {
      setMvhColors(mvhColors)
    }

    /**
     * embedForm
     * @description this is the main API entry point to show the form
     * @param {Object} formDetails - details on the form to be generated (name, contents, colors, etc.)
     */
    // @ts-ignore
    window._pureCarsLeadForm.embedForm = (
      formDetails: FormDetailsType
    ): Promise<void> => {
      validateFormDetailParameters(formDetails)
      return ensureFormDetailsAccountId(formDetails).then(_formDetails => {
        setActiveEmbedFormDetails(_formDetails)
        return Promise.resolve()
      })
    }

    /**
     * showForm
     * @description this is the main API entry point to show the form
     * @param {Object} formDetails - details on the form to be generated (name, contents, colors, etc.)
     * @param {Boolean} includePriceDrops - whether to include a price drop subscription with the modal submission
     * @param {Boolean} sendToPhone - if true, whether to send details of the submitted vehicle to phone as well
     * @return {Promise} - resolves when the form is dismissed
     */
    // @ts-ignore
    window._pureCarsLeadForm.showForm = (
      formDetails: FormDetailsType,
      includePriceDrops = false,
      sendToPhone = false
    ): Promise<NativeLeadType | undefined> => {
      validateFormDetailParameters(formDetails)
      return new Promise((resolve, reject) => {
        if (!promiseResolveRef.current) {
          promiseResolveRef.current = (lead: NativeLeadType | undefined) => {
            promiseResolveRef.current = undefined
            resolve(lead)
          }
          ensureFormDetailsAccountId(formDetails)
            .then(_formDetails => {
              setActiveModalFormDetails(_formDetails)
            })
            .catch(e => reject(e))

          setModalExtraFlags(
            (includePriceDrops ? EXTRA_SUBSCRIBE_TO_PRICE_DROPS : 0) |
              (sendToPhone ? EXTRA_SEND_TO_PHONE : 0)
          )
        } else {
          debugLogForms('not showing additional modal - already displaying')
          resolve(undefined)
        }
      })
    }
  }, [
    setActiveEmbedFormDetails,
    setActiveModalFormDetails,
    setModalExtraFlags,
    setMvhColors,
    submittedModalLeadRef
  ])

  // this effect handles resolving the showForm promise when the active form is dismissed
  useEffect(() => {
    if (!activeModalFormDetails && promiseResolveRef.current) {
      promiseResolveRef.current(submittedModalLeadRef.current)
    }
  }, [activeModalFormDetails, submittedModalLeadRef])
}
